import React, { useState, useEffect } from 'react';
import { getMyPromotions, getCategories, getSocieties, deletePromotion } from '../services/api';
import { Promotion, PromotionCategory } from '../services/api';
import PromotionForm, { PromotionFormData } from '../components/PromotionForm';
import PromotionPreview from '../components/PromotionPreview';
import './CustomerPromotions.css';

const CustomerPromotions: React.FC = () => {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [categories, setCategories] = useState<PromotionCategory[]>([]);
  const [societies, setSocieties] = useState<Array<{ id: number; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
  const [previewData, setPreviewData] = useState<PromotionFormData | null>(null);

  const load = async () => {
    try {
      const [promoRes, catRes, socRes] = await Promise.all([
        getMyPromotions(),
        getCategories(),
        getSocieties(),
      ]);
      setPromotions(promoRes.data);
      setCategories(catRes.data);
      setSocieties(socRes.data);
    } catch {
      setPromotions([]);
      setCategories([]);
      setSocieties([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleClose = () => {
    setShowForm(false);
    setEditingPromotion(null);
    setPreviewData(null);
  };

  const handleSave = () => {
    load();
    handleClose();
  };

  const handleEdit = (p: Promotion) => {
    setEditingPromotion(p);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this promotion?')) return;
    try {
      await deletePromotion(id);
      setPromotions((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      alert('Failed to delete');
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = { story: 'Story', spotlight: 'Spotlight', category: 'Category' };
    return labels[type] || type;
  };

  if (loading) {
    return <div className="customer-promotions-loading">Loading...</div>;
  }

  return (
    <div className="customer-promotions">
      <div className="customer-promotions-header">
        <h2>My Promotions</h2>
        <button
          type="button"
          className="btn-add-promo"
          onClick={() => {
            setEditingPromotion(null);
            setShowForm(true);
          }}
        >
          + Create Promotion
        </button>
      </div>

      {showForm ? (
        <div className="create-with-preview">
          <div className="create-form-column">
            <PromotionForm
              promotion={editingPromotion}
              categories={categories}
              societies={societies}
              onClose={handleClose}
              onSave={handleSave}
              onFormChange={setPreviewData}
              inline
            />
          </div>
          <div className="create-preview-column">
            <PromotionPreview data={previewData} />
          </div>
        </div>
      ) : (
        <>
          {promotions.length === 0 ? (
            <div className="no-promotions">
              <p>You haven't created any promotions yet.</p>
              <button type="button" className="btn-add-promo" onClick={() => setShowForm(true)}>
                Create your first promotion
              </button>
            </div>
          ) : (
            <div className="promotions-list-wrap">
              <table className="promotions-table">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Brand</th>
                    <th>Pricing</th>
                    <th>Valid</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {promotions.map((p) => (
                    <tr key={p.id}>
                      <td>{p.title}</td>
                      <td><span className={`type-badge type-${p.promotion_type}`}>{getTypeLabel(p.promotion_type)}</span></td>
                      <td>{p.brand_name || '–'}</td>
                      <td className="pricing-cell">
                        {(p as Promotion & { stat_prices?: { stat_parameter: string; price_per_unit: number }[] }).stat_prices?.length
                          ? ((p as Promotion & { stat_prices?: { stat_parameter: string; price_per_unit: number }[] }).stat_prices ?? [])
                              .map((s) => `${s.stat_parameter} ${s.price_per_unit}`)
                              .join(' · ')
                          : '–'}
                      </td>
                      <td>{new Date(p.valid_from).toLocaleDateString()} – {new Date(p.valid_to).toLocaleDateString()}</td>
                      <td>
                        <button type="button" className="btn-edit-sm" onClick={() => handleEdit(p)}>Edit</button>
                        <button type="button" className="btn-delete-sm" onClick={() => handleDelete(p.id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CustomerPromotions;
